import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useSettingsStore } from '../stores/settingsStore';
import { isProductFieldOptedIn } from '../utils/productFieldVisibility';

/**
 * Nome commerciale degli articoli (products.commercial_name) da mostrare accanto
 * al nome magazzino negli ordini.
 *
 * Personalizzabile per organizzazione con la voce già esistente «Nome commerciale»
 * in Impostazioni → Magazzino → Campi prodotto: si vede SOLO se l'org l'ha
 * accesa esplicitamente (isProductFieldOptedIn). Le org che non l'hanno mai
 * attivata non fanno query e non vedono nulla di diverso.
 *
 * Vale anche con «Nome commerciale sui documenti» acceso
 * (warehouse_settings.commercial_name_on_documents, Say Farm): preventivo, DDT e
 * fattura riportano il nome commerciale, quindi chi prepara l'ordine deve
 * vederlo accanto al nome magazzino per riconoscere l'articolo.
 */
export function useCommercialNameVisible(): boolean {
  const { settings } = useSettingsStore();
  const onDocuments =
    (settings as { warehouse_settings?: { commercial_name_on_documents?: boolean } | null } | null)
      ?.warehouse_settings?.commercial_name_on_documents === true;
  return onDocuments || isProductFieldOptedIn(settings, 'commercial_name');
}

/** Legge i nomi commerciali degli articoli indicati (solo se la voce è attiva). */
export async function fetchCommercialNames(productIds: Array<string | null | undefined>): Promise<Record<string, string>> {
  const ids = [...new Set(productIds.filter((id): id is string => !!id))];
  const result: Record<string, string> = {};
  // A blocchi: una lista ordini può riferire molti articoli e l'URL ha un limite
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase
      .from('products')
      .select('id, commercial_name')
      .in('id', ids.slice(i, i + 150));
    if (error) {
      console.warn('[commercialNames] nomi commerciali non caricati', error);
      continue;
    }
    for (const p of (data || []) as Array<{ id: string; commercial_name: string | null }>) {
      if (p.commercial_name?.trim()) result[p.id] = p.commercial_name.trim();
    }
  }
  return result;
}

export function useProductCommercialNames(productIds: Array<string | null | undefined>) {
  const enabled = useCommercialNameVisible();
  const key = useMemo(
    () => [...new Set(productIds.filter((id): id is string => !!id))].sort().join(','),
    [productIds],
  );
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!enabled || !key) { setNames({}); return; }
    let cancelled = false;
    fetchCommercialNames(key.split(',')).then(n => { if (!cancelled) setNames(n); });
    return () => { cancelled = true; };
  }, [enabled, key]);

  return { enabled, commercialNameFor: (productId?: string | null) => (enabled && productId ? names[productId] : undefined) };
}
